import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import DeleteAccountModal from "./DeleteAccountModal";
import PaymentMethodsModal from "./PaymentMethodsModal";
import { getSosList, getReferDetails, getReportIssueReason, logOut, userUpdatePassword, getUserDetails, editUserDetails } from "../../redux/actions/action-creator";
import { selectConfigurationData } from "../../redux/selectors/configurationSelectors";
import { countriesData } from "../../constant/countries";
import { getAppUrl } from "../../utils/appUrl";
import "./MyAccount.css";

const menuItems = [
  { label: "Personal Details", icon: "ph-user" },
  //{ label: "Payment Methods", icon: "ph-credit-card" },
  { label: "Emergency Contacts", icon: "ph-phone-call" },
  { label: "Invite Friends", icon: "ph-user-plus" },
  { label: "Reported Issues", icon: "ph-clipboard-text" },
  //{ label: "Contact Us", icon: "ph-envelope-simple" },
  //{ label: "About Us", icon: "ph-info" },
  //{ label: "Privacy Policy", icon: "ph-shield-check" },
  //{ label: "Term and Services", icon: "ph-file-text" },
  //{ label: "Support", icon: "ph-headset" },
  { label: "My Addresses", icon: "ph-map-pin" },
  { label: "Update Password", icon: "ph-password" },
  //{ label: "Help Center", icon: "ph-question" },
  //{ label: "Notification", icon: "ph-bell" },
  //{ label: "Delete Account", icon: "ph-trash" },
  { label: "Logout", icon: "ph-sign-out" },
];

const getDisplayValue = (...values) =>
  values.find((value) => value !== null && value !== undefined && value !== "") || "";

const normalizePhoneCode = (value, fallback = "+91") => {
  const rawValue = getDisplayValue(value);

  if (!rawValue) {
    return fallback;
  }

  const normalizedValue = String(rawValue).trim();
  const matchedCountry = countriesData.find(
    (country) =>
      country.phonecode === normalizedValue ||
      country.country_code === normalizedValue.toUpperCase()
  );

  if (matchedCountry) {
    return matchedCountry.phonecode;
  }

  if (/^\d+$/.test(normalizedValue)) {
    return `+${normalizedValue}`;
  }

  return normalizedValue.startsWith("+") ? normalizedValue : fallback;
};

const getPhoneCodeWithPlus = (value) => normalizePhoneCode(value, "");

const buildPhonePayloadValue = (countryCode, phoneNumber) => {
  const code = getPhoneCodeWithPlus(countryCode);
  const number = String(phoneNumber || "").replace(/\D/g, "");

  return code || number ? `${code}${number}` : "";
};

const buildCountryCodePayloadValue = (countryCode) => {
  const code = getPhoneCodeWithPlus(countryCode);

  return code || "";
};

const splitName = (name = "") => {
  const parts = String(name).trim().split(" ").filter(Boolean);
  return {
    firstName: parts[0] || "",
    lastName: parts.slice(1).join(" "),
  };
};

const normalizeDateValue = (value) => {
  if (typeof value !== "string") {
    return "";
  }

  const normalized = value.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
    return normalized;
  }

  const ddmmyyyy = normalized.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
  if (ddmmyyyy) {
    const [, day, month, year] = ddmmyyyy;
    return `${year}-${month}-${day}`;
  }

  const parsed = new Date(normalized);
  if (!Number.isNaN(parsed.getTime())) {
    const year = parsed.getFullYear();
    const month = `${parsed.getMonth() + 1}`.padStart(2, "0");
    const day = `${parsed.getDate()}`.padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  return "";
};

const getNationalityString = (value) => {
  if (typeof value === "string") {
    return value;
  }

  if (typeof value === "object" && value !== null) {
    return (
      value?.name ||
      value?.label ||
      value?.value ||
      value?.nationality ||
      value?.country_name ||
      ""
    );
  }

  if (value || value === 0) {
    return String(value);
  }

  return "";
};

const normalizeNationalityOptions = (configurationData) => {
  if (!configurationData || typeof configurationData !== "object") {
    return [];
  }

  const candidateArrays = [
    configurationData?.nationalities,
    configurationData?.nationality_list,
    configurationData?.nationalityList,
    configurationData?.nationalityOptions,
    configurationData?.nationality_options,
    configurationData?.country_nationalities,
    configurationData?.countries,
    configurationData?.country_list,
    configurationData?.countryList,
    configurationData?.data?.nationalities,
    configurationData?.data?.nationality_list,
    configurationData?.response_data?.nationalities,
    configurationData?.response_data?.nationality_list,
  ];

  const nationalityList = candidateArrays.find(Array.isArray) || [];

  return nationalityList
    .map((item) => {
      const label = getNationalityString(item);
      const id = getDisplayValue(
        item?.id,
        item?.nationality_id,
        item?.nationalityId,
        item?.country_id,
        item?.countryId,
        item?.value,
        label
      );

      return label && id ? { id: String(id), label } : null;
    })
    .filter(Boolean)
    .reduce((unique, item) => {
      if (!unique.some((option) => option.id === item.id)) {
        unique.push(item);
      }
      return unique;
    }, []);
};

const getNationalityOptionId = (value, options = []) => {
  const normalizedValue = getDisplayValue(value);

  if (!normalizedValue) {
    return "";
  }

  const stringValue = String(normalizedValue);
  const matchedOption = options.find(
    (option) => option.id === stringValue || option.label === stringValue
  );

  return matchedOption?.id || stringValue;
};

const fallbackEmergencyContacts = [
  { id: "police", name: "Police", number: "100", canDelete: false },
  { id: "ambulance", name: "Ambulance", number: "102", canDelete: true },
];

const sosRequestPayload = { merchant_id: 1 };

const fallbackReportedIssues = [
  {
    id: "reported-issue-1",
    orderId: "#102655",
    reportedIssue: "Add Item Request",
    createdAt: "2026-03-11 18:15:57",
    description:
      "Report any problems or errors you're experiencing so we can fix them quickly.",
    adminComment:
      "Submit details about any issue you encounter. Our support team will review.",
    status: "Pending",
    images: [],
  },
  {
    id: "reported-issue-2",
    orderId: "",
    reportedIssue: "",
    createdAt: "",
    description: "",
    adminComment: "",
    status: "Pending",
    images: [],
  },
  {
    id: "reported-issue-3",
    orderId: "",
    reportedIssue: "",
    createdAt: "",
    description: "",
    adminComment: "",
    status: "Pending",
    images: [],
  },
];

const fallbackInviteDetails = {
  bannerImage: "",
  referralCode: "GOPI6589",
  heading: "Refer Code",
  message:
    "Thank you for your interest. Unfortunately, the referral program is currently not available for your account or in your region.",
  offer: "",
  status: "DEACTIVE",
  startDate: "",
  endDate: "",
  shareText: "Share Your Code",
};

const isSuccessResult = (value) => value === true || value === 1 || value === "1";

const getSosContactsFromResponse = (response) => {
  const possibleContacts = [
    response?.data,
    response?.data?.data,
    response?.data?.data?.sos,
    response?.data?.data?.sos_list,
    response?.data?.data?.emergency_contacts,
    response?.data?.response_data,
    response?.data?.sos,
    response?.data?.sos_list,
    response?.data?.sosList,
    response?.data?.emergency_contacts,
    response?.data?.emergencyContacts,
    response?.data?.list,
    response?.response_data,
    response?.sos,
    response?.sos_list,
    response?.sosList,
    response?.emergency_contacts,
    response?.emergencyContacts,
    response?.list,
  ];

  return possibleContacts.find(Array.isArray) || [];
};

const normalizeEmergencyContacts = (contacts, fallbackContacts = fallbackEmergencyContacts) => {
  if (!Array.isArray(contacts)) {
    return fallbackContacts;
  }

  const normalizedContacts = contacts
    .map((contact, index) => ({
      id: getDisplayValue(
        contact?.id,
        contact?._id,
        contact?.sos_id,
        contact?.emergency_id,
        `emergency-${index}`
      ),
      name: getDisplayValue(
        contact?.name,
        contact?.title,
        contact?.contact_name,
        contact?.person_name,
        contact?.label
      ),
      number: getDisplayValue(
        contact?.number,
        contact?.phone,
        contact?.mobile,
        contact?.contact_number,
        contact?.mobile_number,
        contact?.phone_number,
        contact?.helpline_no,
        contact?.sos_number
      ),
      canDelete: Boolean(
        contact?.canDelete ?? contact?.can_delete ?? contact?.isCustom ?? contact?.is_custom
      ),
    }))
    .filter((contact) => contact.name && contact.number);

  return normalizedContacts.length > 0 ? normalizedContacts : fallbackContacts;
};

const normalizeReportedIssues = (issues) => {
  if (!Array.isArray(issues)) {
    return fallbackReportedIssues;
  }

  const normalizedIssues = issues.map((issue, index) => {
    const imageFields = [
      issue?.image,
      issue?.image_1,
      issue?.image_2,
      issue?.issue_image,
      issue?.issue_image_1,
      issue?.issue_image_2,
    ].filter(Boolean);

    const rawImages =
      (Array.isArray(issue?.images) ? issue.images : [])
        .concat(Array.isArray(issue?.attachments) ? issue.attachments : [])
        .concat(Array.isArray(issue?.issue_images) ? issue.issue_images : [])
        .concat(imageFields);

    const issueImages = rawImages.filter(Boolean);

    return {
      id: getDisplayValue(issue?.id, issue?._id, `reported-issue-${index}`),
      orderId: getDisplayValue(
        issue?.order_id,
        issue?.orderId,
        issue?.order?.id,
        issue?.order_number,
        issue?.orderNumber
      ),
      reportedIssue: getDisplayValue(
        issue?.reported_issues,
        issue?.reportedIssue,
        issue?.reported_issue,
        issue?.issue,
        issue?.title,
        issue?.type
      ),
      createdAt: getDisplayValue(
        issue?.created_at,
        issue?.createdAt,
        issue?.date,
        issue?.created
      ),
      description: getDisplayValue(
        issue?.user_comments,
        issue?.userComments,
        issue?.description,
        issue?.message,
        issue?.details
      ),
      adminComment: getDisplayValue(
        issue?.admin_comments,
        issue?.admin_comment,
        issue?.adminComment,
        issue?.comment,
        issue?.support_comment
      ),
      status: getDisplayValue(issue?.status, "Pending"),
      images: issueImages,
    };
  });

  return normalizedIssues;
};

const normalizeInviteDetails = (inviteData = {}) => ({
  bannerImage: getDisplayValue(
    inviteData?.banner_image,
    inviteData?.bannerImage,
    inviteData?.image,
    inviteData?.referral_banner
  ),
  referralCode: getDisplayValue(
    inviteData?.referral_code,
    inviteData?.referralCode,
    inviteData?.code,
    inviteData?.coupon_code,
    inviteData?.refer_code,
    fallbackInviteDetails.referralCode
  ),
  heading: getDisplayValue(
    inviteData?.title,
    inviteData?.heading,
    inviteData?.refer_heading,
    fallbackInviteDetails.heading
  ),
  message: getDisplayValue(
    inviteData?.message,
    inviteData?.description,
    inviteData?.note,
    inviteData?.refer_explanation,
    fallbackInviteDetails.message
  ),
  offer: getDisplayValue(
    inviteData?.offer,
    inviteData?.refer_offer,
    fallbackInviteDetails.offer
  ),
  status: getDisplayValue(
    inviteData?.status,
    inviteData?.refer_status,
    fallbackInviteDetails.status
  ),
  startDate: getDisplayValue(
    inviteData?.start_date,
    inviteData?.startDate,
    inviteData?.valid_from,
    fallbackInviteDetails.startDate
  ),
  endDate: getDisplayValue(
    inviteData?.end_date,
    inviteData?.endDate,
    inviteData?.valid_to,
    fallbackInviteDetails.endDate
  ),
  shareText: getDisplayValue(
    inviteData?.share_text,
    inviteData?.shareText,
    inviteData?.button_text,
    fallbackInviteDetails.shareText
  ),
});

const getFirstResponseRecord = (value) => {
  if (Array.isArray(value)) {
    return value.find((item) => item && typeof item === "object") || {};
  }

  return value && typeof value === "object" ? value : {};
};

const getResponseRecord = (response) => {
  const candidates = [
    response?.data?.response_data,
    response?.data?.data,
    response?.data?.user,
    response?.data?.profile,
    response?.response_data,
    response?.user,
    response?.profile,
    response?.data,
    response,
  ];

  const matchedCandidate = candidates.find(
    (candidate) => candidate !== null && candidate !== undefined && candidate !== ""
  );

  return getFirstResponseRecord(matchedCandidate);
};

const MyAccount = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userData = {} } = useSelector((state) => state.auth || {});
  const {
    sosListResponse = null,
    sosListLoading = false,
    sosListError = null,
  } = useSelector((state) => state.common || {});
  const hasSosListRequest = Boolean(sosListResponse || sosListLoading || sosListError);

  const emergencyContacts = useMemo(() => {
    if (isSuccessResult(sosListResponse?.result)) {
      return normalizeEmergencyContacts(getSosContactsFromResponse(sosListResponse), []);
    }

    if (hasSosListRequest) {
      return [];
    }

    return normalizeEmergencyContacts(
      userData?.emergency_contacts ||
        userData?.emergencyContacts ||
        userData?.emergency_numbers ||
        userData?.emergencyNumbers
    );
  }, [hasSosListRequest, sosListResponse, userData]);

  const [profile, setProfile] = useState({
    firstName: "",
    lastName: "",
    countryCode: "",
    mobile: "",
    whatsapp_countryCode: "",
    whatsapp_number: "",
    email: "",
    dateOfBirth: "",
    nationality: "",
    avatar: `${process.env.PUBLIC_URL}/assets/images/thumbs/comment-img1.png`,
  });
  const [passwordForm, setPasswordForm] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordStatus, setPasswordStatus] = useState({ type: "", message: "" });
  const [activeSection, setActiveSection] = useState("Personal Details");
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [showPaymentMethodsModal, setShowPaymentMethodsModal] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("cybersource");
  const [profileSaving, setProfileSaving] = useState(false);
  const [selectedProfileImage, setSelectedProfileImage] = useState(null);
  const [inviteData, setInviteData] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState(null);
  const [reportedIssuesData, setReportedIssuesData] = useState(null);
  const [reportedIssuesLoading, setReportedIssuesLoading] = useState(false);
  const [reportedIssuesError, setReportedIssuesError] = useState(null);
  const [userDetailsLoading, setUserDetailsLoading] = useState(false);

  const configurationData = useSelector(selectConfigurationData);
  const clientContext = useSelector((state) => state.common?.clientContext || {});
  const dateOfBirthInputRef = useRef(null);
  const profileImageInputRef = useRef(null);

  const openDatePicker = () => {
    const input = dateOfBirthInputRef.current;
    if (!input) {
      return;
    }

    input.focus();
    if (typeof input.showPicker === "function") {
      input.showPicker();
    }
  };

  const openProfileImagePicker = () => {
    profileImageInputRef.current?.click();
  };

  const handleProfileImageChange = (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    if (file.type && !file.type.startsWith("image/")) {
      toast.warning("Please choose a valid image.");
      event.target.value = "";
      return;
    }

    setSelectedProfileImage(file);

    const reader = new FileReader();
    reader.onload = () => {
      setProfile((previousProfile) => ({
        ...previousProfile,
        avatar: reader.result || previousProfile.avatar,
      }));
    };
    reader.readAsDataURL(file);
  };

  const nationalityOptions = useMemo(() => {
    const options = normalizeNationalityOptions(configurationData);
    return options.length > 0
      ? options
      : [
          { id: "Indian", label: "Indian" },
          { id: "United Arab Emirates", label: "United Arab Emirates" },
          { id: "United Kingdom", label: "United Kingdom" },
          { id: "United States", label: "United States" },
        ];
  }, [configurationData]);

  useEffect(() => {
    setProfile((previousProfile) => {
      const nationalityId = getNationalityOptionId(previousProfile.nationality, nationalityOptions);

      if (!nationalityId || nationalityId === previousProfile.nationality) {
        return previousProfile;
      }

      return {
        ...previousProfile,
        nationality: nationalityId,
      };
    });
  }, [nationalityOptions]);

  const selectedCountry =
    countriesData.find((country) => country.phonecode === profile.countryCode) ||
    countriesData[0];

  const fetchUserDetailsFromAPI = () => {
    if (userDetailsLoading) {
      return;
    }

    setUserDetailsLoading(true);

    const payload = {
      ...clientContext,
      apk_version: "4.14",
      merchant_id: 1,
    };

    dispatch(getUserDetails(payload))
      .then((response) => {
        if (isSuccessResult(response?.result)) {
          const userData = getResponseRecord(response);
          const parsedName = splitName(
            getDisplayValue(userData?.name, userData?.full_name, userData?.user_name)
          );
          const countryCode = normalizePhoneCode(
            getDisplayValue(userData?.phone_code, userData?.phonecode, userData?.country_dial_code),
            ""
          );

          setProfile((previousProfile) => ({
            ...previousProfile,
            firstName: getDisplayValue(userData?.first_name, parsedName.firstName, ""),
            lastName: getDisplayValue(userData?.last_name, parsedName.lastName, ""),
            countryCode,
            mobile: getDisplayValue(userData?.UserPhone, userData?.mobile, userData?.phone, ""),
            whatsapp_countryCode: normalizePhoneCode(
              getDisplayValue(
                userData?.whatsapp_countryCode,
                userData?.whatsapp_country_code,
                userData?.whatsapp_phone_code,
                userData?.whatsapp_phonecode,
                userData?.whatsapp_country_dial_code,
                userData?.whatsappCountryCode
              ),
              previousProfile.whatsapp_countryCode || ""
            ),
            whatsapp_number: getDisplayValue(
              userData?.whatsapp_number,
              userData?.whatsappNumber,
              userData?.whatsapp_no,
              userData?.whatsapp,
              userData?.UserWhatsappNumber,
              previousProfile.whatsapp_number
            ),
            email: getDisplayValue(userData?.email, ""),
            dateOfBirth: normalizeDateValue(
              getDisplayValue(userData?.date_of_birth, userData?.dob, "")
            ),
            nationality: getNationalityOptionId(
              getDisplayValue(
                userData?.nationality_id,
                userData?.nationalityId,
                userData?.country_id,
                userData?.countryId,
                userData?.nationality,
                userData?.country_name === "India" ? "Indian" : userData?.country_name
              ),
              nationalityOptions
            ),
            avatar:
              getDisplayValue(
                userData?.UserProfileImage,
                userData?.profile_image,
                userData?.profileImage,
                userData?.image,
                userData?.avatar
              ) || `${process.env.PUBLIC_URL}/assets/images/thumbs/comment-img1.png`,
          }));
        }
      })
      .catch((error) => {
        console.log("Error fetching user details:", error?.message || error);
      })
      .finally(() => {
        setUserDetailsLoading(false);
      });
  };

  const reportedIssues = useMemo(
    () =>
      normalizeReportedIssues(
        reportedIssuesData ||
          userData?.reported_issues ||
          userData?.reportedIssues ||
          userData?.issues
      ),
    [reportedIssuesData, userData]
  );

  const inviteDetails = useMemo(
    () =>
      normalizeInviteDetails(
        inviteData ||
          userData?.invite_friends ||
          userData?.inviteFriends ||
          userData?.referral ||
          userData?.referral_program ||
          userData?.referralProgram
      ),
    [inviteData, userData]
  );

  useEffect(() => {
    if (activeSection === "Personal Details" && !userDetailsLoading) {
      fetchUserDetailsFromAPI();
    }
  }, [activeSection]);

  const fetchReferDetails = () => {
    if (inviteLoading) {
      return;
    }

    setInviteLoading(true);
    setInviteError(null);

    dispatch(getReferDetails({ merchant_id: 1 }))
      .then((response) => {
        if (isSuccessResult(response?.result)) {
          setInviteData(getResponseRecord(response));
        } else {
          setInviteError(response?.message || "Unable to load referral details.");
        }
      })
      .catch((error) => {
        setInviteError(
          error?.message || error?.data?.message || "Unable to load referral details."
        );
      })
      .finally(() => {
        setInviteLoading(false);
      });
  };

  const fetchReportedIssues = () => {
    if (reportedIssuesLoading) {
      return;
    }

    setReportedIssuesLoading(true);
    setReportedIssuesError(null);

    const formData = new FormData();
    formData.append("segment_slug", "GROCERY");
    formData.append("merchant_id", 1);

    dispatch(getReportIssueReason(formData))
      .then((response) => {
        if (isSuccessResult(response?.result)) {
          setReportedIssuesData(
            response?.data?.response_data ?? response?.response_data ?? response?.data ?? response
          );
        } else {
          setReportedIssuesError(response?.message || "Unable to load reported issues.");
        }
      })
      .catch((error) => {
        setReportedIssuesError(
          error?.message || error?.data?.message || "Unable to load reported issues."
        );
      })
      .finally(() => {
        setReportedIssuesLoading(false);
      });
  };

  useEffect(() => {
    if (activeSection === "Invite Friends" && !inviteData && !inviteLoading) {
      fetchReferDetails();
    }
  }, [activeSection, inviteData, inviteLoading]);

  const handleChange = (field) => (event) => {
    setProfile((previousProfile) => ({
      ...previousProfile,
      [field]: event.target.value,
    }));
  };

  const handlePasswordChange = (field) => (event) => {
    setPasswordForm((previousPasswordForm) => ({
      ...previousPasswordForm,
      [field]: event.target.value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (profileSaving) {
      return;
    }

    const requiredFields = [
      { key: "firstName", message: "First name is required." },
      { key: "lastName", message: "Last name is required." },
      { key: "countryCode", message: "Country code is required." },
      { key: "mobile", message: "Mobile number is required." },
      { key: "email", message: "Email is required." },
      { key: "dateOfBirth", message: "Date of birth is required." },
      { key: "nationality", message: "Nationality is required." },
    ];

    const missingField = requiredFields.find(
      ({ key }) => !String(profile[key] ?? "").trim()
    );

    if (missingField) {
      toast.warning(missingField.message);
      return;
    }

    const payload = selectedProfileImage ? new FormData() : new URLSearchParams();
    payload.append("phone", buildPhonePayloadValue(profile.countryCode, profile.mobile));
    payload.append("dob", profile.dateOfBirth);
    payload.append("nationality_id", profile.nationality);
    payload.append("last_name", profile.lastName.trim());
    payload.append("user_gender", "");
    payload.append("first_name", profile.firstName.trim());
    payload.append("email", profile.email.trim());
    payload.append(
      "whatsapp_countryCode",
      profile.whatsapp_countryCode
        ? buildCountryCodePayloadValue(profile.whatsapp_countryCode)
        : ""
    );
    payload.append("whatsapp_number", String(profile.whatsapp_number || "").trim());
    payload.append("merchant_id", "1");

    if (selectedProfileImage) {
      payload.append("profile_image", selectedProfileImage);
    }

    setProfileSaving(true);

    dispatch(editUserDetails(payload))
      .then((response) => {
        const message =
          response?.message || response?.data?.message || "Personal details updated successfully.";
        setSelectedProfileImage(null);
        toast.success(message);
      })
      .catch((error) => {
        const message =
          error?.message || error?.data?.message || "Unable to update personal details.";
        toast.error(message);
      })
      .finally(() => {
        setProfileSaving(false);
      });
  };

  const handlePasswordSubmit = (event) => {
    event.preventDefault();

    const { oldPassword, newPassword, confirmPassword } = passwordForm;
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

    if (!oldPassword || !newPassword || !confirmPassword) {
      toast.error("Please complete all password fields.");
      return;
    }

    if (!passwordRegex.test(newPassword)) {
      toast.warning(
        "Please enter password use a combination of upper and lowercase letters, numbers, and special symbols"
      );
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("New password and confirm password must match.");
      return;
    }

    setPasswordStatus({ type: "", message: "" });

    const payload = {
      new_password: newPassword,
      old_password: oldPassword,
      merchant_id: 1,
    };

    dispatch(userUpdatePassword(payload))
      .then((response) => {
        const message =
          response?.message || response?.data?.message || "Password updated successfully.";
        setPasswordStatus({ type: "success", message });
        toast.success(message);
        setPasswordForm({ oldPassword: "", newPassword: "", confirmPassword: "" });
      })
      .catch((error) => {
        const message =
          error?.message || error?.data?.message || "Unable to update password.";
        setPasswordStatus({ type: "error", message });
        toast.error(message);
      });
  };

  const handleCopyInviteCode = () => {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText(inviteDetails.referralCode).catch(() => {});
    }
  };

  const handleShareInviteCode = () => {
    const signupLink = getAppUrl(`/signUp?code=${encodeURIComponent(inviteDetails.referralCode)}`);

    if (typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText(signupLink).catch(() => {});
    }

    if (typeof navigator !== "undefined" && navigator.share) {
      navigator.share({
        title: "Invite Friends",
        text: signupLink,
      }).catch(() => {});
    }
  };

  const handleLogout = () => {
    if (typeof window !== "undefined") {
      const selectedEnvironment = localStorage.getItem("rghtnow_api_environment");
      localStorage.clear();
      if (selectedEnvironment) {
        localStorage.setItem("rghtnow_api_environment", selectedEnvironment);
      }
      localStorage.setItem("forceLogout", String(Date.now()));
    }

    dispatch(logOut());
    navigate("/login");
  };

  const fetchEmergencyContacts = () => {
    dispatch(getSosList(sosRequestPayload)).catch(() => {});
  };

  const handleMenuClick = (label) => {
    if (label === "Payment Methods") {
      setShowPaymentMethodsModal(true);
      return;
    }

    if (label === "Delete Account") {
      setShowDeleteAccountModal(true);
      return;
    }

    if (label === "Logout") {
      handleLogout();
      return;
    }

    if (label === "My Addresses") {
      navigate("/address-list");
      return;
    }

    if (label === "Emergency Contacts") {
      setActiveSection(label);
      fetchEmergencyContacts();
      return;
    }

    if (label === "Invite Friends") {
      setActiveSection(label);
      fetchReferDetails();
      return;
    }

    if (
      label === "Personal Details" ||
      label === "Reported Issues" ||
      label === "Update Password"
    ) {
      setActiveSection(label);

      if (label === "Reported Issues") {
        fetchReportedIssues();
      }
    }
  };

  const renderPersonalDetails = () => (
    <form
      className="my-account-content my-account-personal-panel"
      onSubmit={handleSubmit}
    >
      <h2 className="my-account-section-title">Personal Details</h2>

      <div className="my-account-avatar-wrap">
        <img
          src={profile.avatar}
          alt="Profile"
          className="my-account-avatar"
          onClick={openProfileImagePicker}
        />
        <input
          ref={profileImageInputRef}
          type="file"
          accept="image/*"
          className="my-account-avatar-input"
          onChange={handleProfileImageChange}
        />
        <button
          type="button"
          className="my-account-avatar-edit"
          aria-label="Edit profile photo"
          onClick={openProfileImagePicker}
        >
          <i className="ph ph-pencil-simple" />
        </button>
      </div>

      <div className="my-account-form-grid">
        <label className="my-account-field">
          <span className="my-account-field-label">First Name</span>
          <input
            type="text"
            value={profile.firstName}
            onChange={handleChange("firstName")}
            placeholder="Enter first name"
          />
        </label>

        <label className="my-account-field">
          <span className="my-account-field-label">Last Name</span>
          <input
            type="text"
            value={profile.lastName}
            onChange={handleChange("lastName")}
            placeholder="Enter last name"
          />
        </label>

        <label className="my-account-field my-account-country-field">
          <span className="my-account-field-label">Country Code</span>
          <select
            value={profile.countryCode}
            onChange={handleChange("countryCode")}
            disabled
            title="Country code cannot be changed"
            className="my-account-country-select"
          >
            <option value="" disabled>
              Select country code
            </option>
            {countriesData.map((country) => (
              <option key={country.id} value={country.phonecode}>
                {`${country.flagEmoji} ${country.name} ${country.phonecode}`}
              </option>
            ))}
          </select>
        </label>

        <label className="my-account-field">
          <span className="my-account-field-label">Mobile Number</span>
          <input
            type="tel"
            value={profile.mobile}
            onChange={handleChange("mobile")}
            readOnly
            title="Mobile number cannot be changed"
            placeholder="Enter mobile number"
          />
        </label>

        <label className="my-account-field my-account-country-field">
          <span className="my-account-field-label">WhatsApp Country Code</span>
          <select
            value={profile.whatsapp_countryCode}
            onChange={handleChange("whatsapp_countryCode")}
            className="my-account-country-select"
          >
            <option value="">WhatsApp country code (optional)</option>
            {countriesData.map((country) => (
              <option key={country.id} value={country.phonecode}>
                {`${country.flagEmoji} ${country.name} ${country.phonecode}`}
              </option>
            ))}
          </select>
        </label>

        <label className="my-account-field">
          <span className="my-account-field-label">WhatsApp Number</span>
          <input
            type="tel"
            value={profile.whatsapp_number}
            onChange={handleChange("whatsapp_number")}
            placeholder="Enter WhatsApp number"
          />
        </label>

        <label className="my-account-field">
          <span className="my-account-field-label">Email</span>
          <input
            type="email"
            value={profile.email}
            onChange={handleChange("email")}
            placeholder="Enter email address"
          />
        </label>

        <label className="my-account-field">
          <span className="my-account-field-label">Date of Birth</span>
          <input
            type="date"
            ref={dateOfBirthInputRef}
            value={profile.dateOfBirth}
            onChange={handleChange("dateOfBirth")}
            placeholder="Select date of birth"
          />
          <button
            type="button"
            className="my-account-input-icon right"
            onClick={openDatePicker}
            aria-label="Open date picker"
          >
            <i className="ph ph-calendar-blank" />
          </button>
        </label>

        <label className="my-account-field my-account-wide-field">
          <span className="my-account-field-label">Nationality</span>
          <select
            value={profile.nationality}
            onChange={handleChange("nationality")}
          >
            <option value="" disabled>
              Select nationality
            </option>
            {nationalityOptions.map((nationality) => (
              <option key={nationality.id} value={nationality.id}>
                {nationality.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <button type="submit" className="my-account-save-btn" disabled={profileSaving}>
        {profileSaving ? "Saving..." : "Save"}
      </button>
    </form>
  );

  const renderEmergencyContacts = () => (
    <section className="my-account-content my-account-emergency-panel">
      <h2 className="my-account-section-title my-account-emergency-title">
        Emergency Contacts
      </h2>

      <div className="my-account-emergency-list">
        {sosListLoading ? (
          <div className="my-account-emergency-row">
            <span className="my-account-emergency-name">Loading...</span>
          </div>
        ) : emergencyContacts.length > 0 ? (
          emergencyContacts.map((contact) => (
            <div className="my-account-emergency-row" key={contact.id}>
              <span className="my-account-emergency-name">{contact.name}</span>
              <span className="my-account-emergency-side">
                <span className="my-account-emergency-number">{contact.number}</span>
                {contact.canDelete && (
                  <span className="my-account-emergency-delete" aria-hidden="true">
                    <i className="ph ph-trash" />
                  </span>
                )}
              </span>
            </div>
          ))
        ) : (
          <div className="my-account-emergency-row">
            <span className="my-account-emergency-name">
              {sosListError ? "Emergency contacts not found." : "No emergency contacts found."}
            </span>
          </div>
        )}
      </div>
    </section>
  );

  const renderInviteBanner = () => {
    if (inviteDetails.bannerImage) {
      return (
        <img
          src={inviteDetails.bannerImage}
          alt="Invite friends"
          className="my-account-invite-banner-image"
        />
      );
    }

    return (
      <div className="my-account-invite-banner-fallback" aria-hidden="true">
        <div className="my-account-invite-banner-text">
          <span>Refer &amp;</span>
          <strong>Earn</strong>
        </div>
        <span className="my-account-invite-megaphone">
          <i className="ph ph-megaphone" />
        </span>
        <span className="my-account-invite-person">
          <span className="my-account-invite-person-hair" />
          <span className="my-account-invite-person-face" />
          <span className="my-account-invite-person-body" />
          <span className="my-account-invite-person-bag" />
        </span>
        <span className="my-account-invite-purple-dot" />
      </div>
    );
  };

  const renderInviteFriends = () => (
    <section className="my-account-content my-account-invite-panel">
      <h2 className="my-account-section-title my-account-invite-title">
        Invite Friends
      </h2>

      <div className="my-account-invite-card">
        {renderInviteBanner()}

        <div className="my-account-invite-code-block">
          <span className="my-account-invite-code-label">
            {inviteDetails.heading || "Referral Code"}
          </span>
          <button
            type="button"
            className="my-account-invite-code"
            onClick={handleCopyInviteCode}
          >
            <span>
              {inviteLoading ? "Loading..." : inviteDetails.referralCode}
            </span>
            <i className="ph ph-copy" />
          </button>
        </div>

        <p className="my-account-invite-message">
          {inviteLoading
            ? "Loading referral details..."
            : inviteError || inviteDetails.message}
        </p>

        {inviteDetails.offer ? (
          <p className="my-account-invite-offer">{inviteDetails.offer}</p>
        ) : null}

        <div className="my-account-invite-dates">
          <div>
            <span>Start Date</span>
            <strong>{inviteDetails.startDate || ""}</strong>
          </div>
          <div>
            <span>End Date</span>
            <strong>{inviteDetails.endDate || ""}</strong>
          </div>
        </div>

        <button
          type="button"
          className="my-account-invite-share"
          onClick={handleShareInviteCode}
        >
          <i className="ph ph-share-network" />
          <span>{inviteDetails.shareText}</span>
        </button>
      </div>
    </section>
  );

  const renderIssueImage = (image, index) => (
    <span className="my-account-issue-image" key={`${image || "placeholder"}-${index}`}>
      {image ? (
        <img src={image} alt={`Reported issue ${index + 1}`} />
      ) : (
        <i className="ph ph-image" />
      )}
    </span>
  );

  const renderReportedIssues = () => (
    <section className="my-account-content my-account-issues-panel">
      <h2 className="my-account-section-title my-account-issues-title">
        Reported Issue
      </h2>

      {reportedIssuesLoading ? (
        <div className="my-account-loading-state">Loading reported issues...</div>
      ) : reportedIssuesError ? (
        <div className="my-account-error-state">{reportedIssuesError}</div>
      ) : reportedIssues.length === 0 ? (
        <div className="my-account-empty-state">
          <p>No reported issues found.</p>
        </div>
      ) : (
        <div className="my-account-issues-list">
          {reportedIssues.map((issue) => {
            const issueImages = [...issue.images.slice(0, 2)];

            while (issueImages.length < 2) {
              issueImages.push("");
            }

            return (
              <article className="my-account-issue-card" key={issue.id}>
                <span className="my-account-issue-status">{issue.status}</span>

                <div className="my-account-issue-images">
                  {issueImages.map(renderIssueImage)}
                </div>

                <dl className="my-account-issue-details">
                  <dt>Order ID:</dt>
                  <dd>{issue.orderId}</dd>

                  <dt>Reported Issue:</dt>
                  <dd>{issue.reportedIssue}</dd>

                  <dt>Created At:</dt>
                  <dd>{issue.createdAt}</dd>

                  <dt>Description:</dt>
                  <dd>{issue.description}</dd>

                  <dt>Admin Comment:</dt>
                  <dd>{issue.adminComment}</dd>
                </dl>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );

  const renderUpdatePassword = () => (
    <form
      className="my-account-content my-account-password-panel"
      onSubmit={handlePasswordSubmit}
    >
      <h2 className="my-account-section-title my-account-password-title">
        Update Password
      </h2>

      <div className="my-account-password-fields">
        <label className="my-account-password-field">
          <span>Old Password</span>
          <span className="my-account-password-input-wrap">
            <i className="ph ph-lock-key" />
            <input
              type="password"
              value={passwordForm.oldPassword}
              onChange={handlePasswordChange("oldPassword")}
              placeholder="******************"
              autoComplete="current-password"
            />
          </span>
        </label>

        <label className="my-account-password-field">
          <span>New Password</span>
          <span className="my-account-password-input-wrap">
            <i className="ph ph-lock-key" />
            <input
              type="password"
              value={passwordForm.newPassword}
              onChange={handlePasswordChange("newPassword")}
              placeholder="******************"
              autoComplete="new-password"
            />
          </span>
        </label>

        <label className="my-account-password-field">
          <span>Confirm Password</span>
          <span className="my-account-password-input-wrap">
            <i className="ph ph-lock-key" />
            <input
              type="password"
              value={passwordForm.confirmPassword}
              onChange={handlePasswordChange("confirmPassword")}
              placeholder="******************"
              autoComplete="new-password"
            />
          </span>
        </label>
      </div>

      <button type="submit" className="my-account-password-update-btn">
        Update
      </button>

      {passwordStatus.message && (
        <div className={`my-account-password-message ${passwordStatus.type}`}>
          {passwordStatus.message}
        </div>
      )}
    </form>
  );

  const renderActiveSection = () => {
    if (activeSection === "Emergency Contacts") {
      return renderEmergencyContacts();
    }

    if (activeSection === "Invite Friends") {
      return renderInviteFriends();
    }

    if (activeSection === "Reported Issues") {
      return renderReportedIssues();
    }

    if (activeSection === "Update Password") {
      return renderUpdatePassword();
    }

    return renderPersonalDetails();
  };

  return (
    <main className="my-account-page">
      <div className="container container-lg">
        <section className="my-account-shell">
          <h1 className="my-account-title">My Account</h1>

          <div className="my-account-layout">
            <aside className="my-account-sidebar" aria-label="My account menu">
              {menuItems.map((item) => (
                <button
                  key={item.label}
                  type="button"
                  className={`my-account-menu-item ${
                    activeSection === item.label ? "is-active" : ""
                  }`}
                  onClick={() => handleMenuClick(item.label)}
                >
                  <span className="my-account-menu-copy">
                    <i className={`ph ${item.icon}`} />
                    <span>{item.label}</span>
                  </span>
                  <i className="ph ph-caret-right my-account-menu-arrow" />
                </button>
              ))}
            </aside>

            {renderActiveSection()}
          </div>
        </section>
      </div>

      <PaymentMethodsModal
        show={showPaymentMethodsModal}
        onClose={() => setShowPaymentMethodsModal(false)}
        onSave={setSelectedPaymentMethod}
        selectedPaymentMethod={selectedPaymentMethod}
      />

      <DeleteAccountModal
        show={showDeleteAccountModal}
        onClose={() => setShowDeleteAccountModal(false)}
      />
    </main>
  );
};

export default MyAccount;
